/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import React, { useEffect } from 'react';
import { CloseIc } from '../console/icons';

/** Console-styled dialog shell used by the billing flows. */
const BillingModal: React.FC<{
  open: boolean;
  title: React.ReactNode;
  onClose: () => void;
  children: React.ReactNode;
  actions?: React.ReactNode;
  wide?: boolean;
  z?: string;
}> = ({ open, title, onClose, children, actions, wide, z = 'z-50' }) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className={`fixed inset-0 ${z} flex items-center justify-center p-4`} role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} aria-hidden="true" />
      <div
        className={`relative ps-card bg-ps-card w-full ${wide ? 'max-w-2xl' : 'max-w-md'} max-h-[90vh] flex flex-col shadow-2xl`}
      >
        <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-ps-border flex-none">
          <h2 className="m-0 text-base font-medium text-ps-text">{title}</h2>
          <div className="flex items-center gap-2">
            {actions}
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="w-10 h-10 grid place-items-center rounded-md text-ps-muted hover:text-ps-text hover:bg-ps-hover [&>svg]:w-5 [&>svg]:h-5"
            >
              <CloseIc />
            </button>
          </div>
        </div>
        <div className="p-5 overflow-y-auto flex-1 flex flex-col gap-4">{children}</div>
      </div>
    </div>
  );
};

export default BillingModal;

/** Amber / blue / red inline notice block (design: 12px padding, 6px radius). */
export const Notice: React.FC<{ tone?: 'warn' | 'info' | 'err' | 'ok'; children: React.ReactNode; className?: string }> = ({
  tone = 'warn',
  children,
  className = '',
}) => {
  const cls = {
    warn: 'bg-ps-warn-bg text-ps-warn',
    info: 'bg-ps-info-bg text-ps-info',
    err: 'bg-ps-err-bg text-ps-err',
    ok: 'bg-ps-ok-bg text-ps-ok',
  }[tone];
  return <div className={`p-3 rounded-md text-[13px] leading-relaxed ${cls} ${className}`}>{children}</div>;
};
